import { PROSPECT_BUDGET_STATE_LABELS, USAGE_METER_INFO, type Money, type ProspectBudgetState, type ProspectCostLine } from '@lia/contracts';
import { formatMoney, formatNumber } from '../../lib/format.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';

// Setter §06.10.5: per-prospect AI/telephony budget. Costs are estimates (spec §16) unless a provider reported them;
// usage without a rate is «sin costo estimado» and is never rendered as $0.

export const PROSPECT_BUDGET_STATE_TONE: Record<ProspectBudgetState, StatusTone> = {
  undefined: 'neutral',
  disabled: 'neutral',
  ok: 'success',
  near: 'warning',
  reached: 'danger',
};

export function ProspectBudgetBadge({ state, className }: { state: ProspectBudgetState; className?: string }) {
  return (
    <StatusBadge tone={PROSPECT_BUDGET_STATE_TONE[state]} className={className}>
      {PROSPECT_BUDGET_STATE_LABELS[state]}
    </StatusBadge>
  );
}

export interface ProspectCostBreakdownProps {
  total: Money;
  estimated: boolean;
  uncostedEvents: number;
  lines: readonly ProspectCostLine[];
  className?: string;
}

const events = (n: number) => `${n} ${n === 1 ? 'evento' : 'eventos'}`;

/** Accumulated cost of one opportunity by meter, with estimate and «sin costo estimado» markers. */
export function ProspectCostBreakdown({ total, estimated, uncostedEvents, lines, className }: ProspectCostBreakdownProps) {
  if (!lines.length) return <p className={className ?? 'text-body text-text-secondary'}>Sin consumo registrado para esta oportunidad.</p>;
  return (
    <div className={className}>
      <p className="text-body text-text-primary">
        <span className="text-body-lg font-semibold">{formatMoney(total)}</span>
        {estimated ? <span className="ml-2 text-meta text-text-secondary">Estimado (no es una factura del proveedor)</span> : null}
      </p>
      {uncostedEvents > 0 ? <p className="text-meta text-text-secondary">Más {events(uncostedEvents)} sin costo estimado: costo desconocido, no cero.</p> : null}
      <table className="mt-2 w-full text-body">
        <caption className="sr-only">Costo por concepto</caption>
        <thead>
          <tr className="text-left text-meta text-text-secondary">
            <th scope="col" className="py-1 font-medium">Concepto</th>
            <th scope="col" className="py-1 text-right font-medium">Cantidad</th>
            <th scope="col" className="py-1 text-right font-medium">Costo</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.meter} className="border-t border-border-subtle">
              <th scope="row" className="py-1 text-left font-normal text-text-primary">{USAGE_METER_INFO[l.meter].label}</th>
              <td className="py-1 text-right text-text-secondary">
                {formatNumber(l.quantity)} {USAGE_METER_INFO[l.meter].unit}
              </td>
              <td className="py-1 text-right">
                {l.cost ? (
                  <span className="text-text-primary">
                    {formatMoney(l.cost)}
                    {l.estimated ? <span className="text-meta text-text-secondary"> · est.</span> : null}
                  </span>
                ) : (
                  <span className="italic text-text-secondary">Sin tarifa</span>
                )}
                {l.cost && l.uncostedEvents > 0 ? <span className="block text-meta text-text-secondary">{events(l.uncostedEvents)} sin costo</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
